<template>
  <VAppBar v-if="hasSelectedRoleOrShift || !isConnectEnabled" app elevation="0">
    <MenuBar
      v-model="isMobileMenuDrawerOpened"
      :show-shift-indicator="showShiftIndicator"
      class="border-b-sm"
    />
  </VAppBar>

  <VMain>
    <div class="flex flex-col h-full">
      <ConsultationBar v-if="hasSelectedShift && isConnectEnabled" />
      <ShiftBanner v-if="shouldShowShiftBanner && isConnectEnabled" />
      <VContainer
        v-if="hasSelectedRoleOrShift || !isConnectEnabled"
        fluid
        class="h-full max-width-1200 p-0"
      >
        <PushNotificationPopup
          :show="showPushNotificationPopup"
          data-testid="notification-popup"
          @close="showPushNotificationPopup = false"
          @enable-notifications-click="onEnableNotifications"
          @prevent-popup-display="onPreventPopup"
        />
        <CallFrame />
        <RouterView />
      </VContainer>
      <ShiftSelectionOverlay v-else-if="isConnectEnabled" />
    </div>
  </VMain>

  <MobileMenuDrawer
    v-model="isMobileMenuDrawerOpened"
    :show-shift-indicator="!showShiftIndicator"
  />
  <CreateEmergencyConsultationDialog ref="emergencyModal" />
</template>

<script setup lang="ts">
import { computed, provide, ref, useTemplateRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { usePluginsStore } from '@/stores/plugins'
import ShiftSelectionOverlay from '@/components/shift/ShiftSelectionOverlay.vue'
import MenuBar from '@/components/navigation/MenuBar.vue'
import CallFrame from '@/components/CallFrame.vue'
import ConsultationBar from '@/components/consultationrequest/ConsultationBar.vue'
import ShiftBanner from '@/components/shift/ShiftBanner.vue'
import MobileMenuDrawer from '@/components/navigation/MobileMenuDrawer.vue'
import CreateEmergencyConsultationDialog from '@/components/navigation/CreateEmergencyConsultationDialog.vue'
import type { OpenEmergencyModalFn } from '@/types/OpenEmergencyModalFn'
import { useShift } from '@/composables/useShift'
import { useCallStore } from '@/stores/call'
import { useCallRestartNotifications } from '@/composables/useCallRestartNotifications'
import useUserStateHandler from '@/composables/useUserStateHandler'
import { useCaseStore } from '@/stores/case'
import { useRepeatWhileMounted } from '@/composables/repeatWhileMounted'
import { getRefreshIntervalCases } from '@/global/environmentGetters'
import PushNotificationPopup from '@/components/notices/PushNotificationPopup.vue'
import { PushNotificationService } from '@/services/PushNotificationService'
import { useUserStore } from '@/stores/user'
import useLocalStoragePerUser, {
  PUSH_NOTIFICATIONS_ACTIVE_KEY,
  PUSH_NOTIFICATIONS_POPUP_KEY
} from '@/composables/useLocalStoragePerUser'
import { useWindowSize } from '@vueuse/core'

const { user } = useUserStore()
const { tryRestoreActiveCall } = useCallStore()
const { shouldShowShiftBanner } = useShift()
useCallRestartNotifications()
const { hasSelectedRoleOrShift, hasSelectedShift } = useUserStateHandler()
const { isConnectEnabled } = storeToRefs(usePluginsStore())
const caseStore = useCaseStore()
const { removeUserFromLocalStorageByKey, addUserToLocalStorageByKey, getUsersByKey } =
  useLocalStoragePerUser()
const { width } = useWindowSize()

// This operation is needed for proper refresh of the Emergency button avaiability

useRepeatWhileMounted(caseStore.refreshCases, getRefreshIntervalCases())

provide<OpenEmergencyModalFn>('openEmergencyModal', (caseId?: string) =>
  emergencyModal.value?.open(caseId)
)

const isMobileMenuDrawerOpened = ref<boolean>(false)
const showPushNotificationPopup = ref<boolean>(initPopup())
const emergencyModal = useTemplateRef('emergencyModal')

const showShiftIndicator = computed(() => width.value >= 542)

// Re-register the FCM token right after login (regardless of which route the
// user lands on) when push notifications were previously enabled on this device.
// Without this, re-registration only happens once the settings card is opened.
PushNotificationService.restoreRegistrationFromLocalPreference()

function initPopup() {
  if (!user?.userName) {
    return false
  }

  const hasDismissedPushNotificationPopup = getUsersByKey(PUSH_NOTIFICATIONS_POPUP_KEY).includes(
    user.userName
  )
  let isPushNotificationsSettingActive = getUsersByKey(PUSH_NOTIFICATIONS_ACTIVE_KEY).includes(
    user.userName
  )

  if (
    isPushNotificationsSettingActive &&
    'Notification' in window &&
    window.Notification.permission !== 'granted'
  ) {
    removeUserFromLocalStorageByKey(PUSH_NOTIFICATIONS_ACTIVE_KEY)
    isPushNotificationsSettingActive = false
  }

  return !hasDismissedPushNotificationPopup && !isPushNotificationsSettingActive
}

function onEnableNotifications() {
  PushNotificationService.activateNotifications()
  showPushNotificationPopup.value = false
}

function onPreventPopup(value: boolean) {
  if (value) {
    addUserToLocalStorageByKey(PUSH_NOTIFICATIONS_POPUP_KEY)
  } else {
    removeUserFromLocalStorageByKey(PUSH_NOTIFICATIONS_POPUP_KEY)
  }
}

watch(
  hasSelectedRoleOrShift,
  async (isReady) => {
    if (isReady) {
      await tryRestoreActiveCall()
    }
  },
  { immediate: true }
)
</script>

<style>
.max-width-1200 {
  max-width: 1200px;
}
</style>
